import React, { useMemo } from 'react';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import { useFormatNumber } from '@broadleaf/admin-components/dist/common';
import useContextParams from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/hooks/useContextParams';
import useEventCallback from '@broadleaf/admin-components/dist/common/hooks/useEventCallback';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import {
  clearFormikErrors,
  setFormikErrors
} from '@broadleaf/admin-components/dist/form/utils/RequestErrorHelpers';
import { Formik } from 'formik';
import * as yup from 'yup';
import { IOrderFulfillment } from '@broadleaf/admin-components/dist/types/oms';
import { UserAccess } from '@broadleaf/admin-components/dist/authentication';
import { SelectField } from '@src/custom/common/form/SelectField';
import { InputField } from '@src/custom/common/form/InputField';
import { SubmitButton } from '@src/custom/common/form/SubmitButton';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentSellerPaymentSummary } from '@src/oms/utils/RmFulfillmentUtils';
import messages from './ManageSellerPaymentsModal.messages';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';

type AddSellerPaymentFormProps = {
  actionDefinition: IMetadataAction;
  metadata: IMetadata;
  state: IFulfillmentViewLocalState;
  refresh: Function;
  fulfillment: IOrderFulfillment;
  sellerPaymentSummary: IFulfillmentSellerPaymentSummary;
  sellerNetBalance: number;
};
export const AddSellerPaymentForm: React.FC<AddSellerPaymentFormProps> = props => {
  const {
    actionDefinition,
    metadata,
    state,
    fulfillment,
    sellerPaymentSummary,
    refresh,
    sellerNetBalance
  } = props;
  const formatMessage = useFormatMessage();
  const formatNumber = useFormatNumber();

  const contextParams = useContextParams(metadata, state);
  const initialValues = useMemo(() => {
    return {
      amount: sellerNetBalance.toFixed(2),
      reason: 'WIRE_TRANSFER',
      reasonDescription: formatMessage(messages.paymentMethods.wire)
    };
  }, [sellerNetBalance]);
  const validationSchema = useMemo(() => {
    return yup.object().shape({
      amount: yup.number().required().min(0).max(sellerNetBalance),
      reason: yup.string().required(),
      reasonDescription: yup.string().required()
    });
  }, [sellerNetBalance]);
  const handleSubmit = useEventCallback(
    async (values, formik) => {
      formik.setSubmitting(true);
      try {
        const submitEndpoint = getEndpointByType(
          actionDefinition,
          'ADD_SELLER_PAYMENT'
        );
        const { data: response } = await request(
          {
            method: 'post',
            data: {
              ...values,
              fulfillmentId: fulfillment.id
            },
            ...submitEndpoint
          },
          contextParams
        );

        formik.setSubmitting(false);
        clearFormikErrors(formik);

        formik.resetForm();
        await refresh();
      } catch (error) {
        setFormikErrors(error, formik);
        formik.setSubmitting(false);
      } finally {
        formik.setSubmitting(false);
      }
    },
    [
      contextParams,
      actionDefinition,
      refresh,
      sellerPaymentSummary,
      fulfillment
    ]
  );
  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {formik => (
          <AddSellerPaymentSection
            formik={formik}
            fulfillment={fulfillment}
            sellerNetBalance={sellerNetBalance}
          />
        )}
      </Formik>
    </>
  );
};

export type AddSellerPaymentSectionProps = {
  formik: any;
  fulfillment: IOrderFulfillment;
  sellerNetBalance: number;
};
export const AddSellerPaymentSection: React.FC<AddSellerPaymentSectionProps> = ({
  formik,
  sellerNetBalance,
  fulfillment
}) => {
  const checkAccess = UserAccess.useCheckAccess();
  const formatNumber = useFormatNumber();
  const formatMessage = useFormatMessage();
  const hasAccess = checkAccess('ORDER_SELLER_PAYMENT', 'UPDATE');

  const isDisabled = useMemo(() => {
    return (
      !hasAccess ||
      sellerNetBalance <= 0 ||
      // we dont allow adding seller payments once we've passed the Seller Payout Pending status or it has been cancelled
      [
        'COMPLETED',
        'CANCELLED',
        'DISPUTED',
        'RETURNED',
        'REFUNDED',
        'SELLER_PAYOUT_PENDING'
      ].includes(fulfillment.status)
    );
  }, [hasAccess, sellerNetBalance, fulfillment]);

  const paymentMethods = [
    {
      label: formatMessage(messages.paymentMethods.revmedCredit),
      value: 'REVMED_CREDIT'
    },
    {
      label: formatMessage(messages.paymentMethods.wire),
      value: 'WIRE_TRANSFER'
    },
    {
      label: formatMessage(messages.paymentMethods.ach),
      value: 'ACH'
    },
    {
      label: formatMessage(messages.paymentMethods.other),
      value: 'OTHER'
    }
  ];
  return (
    <>
      <div className="tw-flex tw-flex-1 tw-flex-col tw-border-b tw-border-gray-500">
        <div className="tw-flex tw-flex-1">
          <div className="tw-flex-1">
            <SelectField
              formik={formik}
              label="Method"
              required
              name="reason"
              placeholder="Choose a Payment Method"
              options={paymentMethods}
              onChange={option => {
                if (option.value !== 'OTHER') {
                  formik.setFieldValue('reasonDescription', option.label);
                } else {
                  formik.setFieldValue('reasonDescription', '');
                }
              }}
              isDisabled={isDisabled}
            />
          </div>
          <div className="tw-flex-1">
            <InputField
              fullWidth={true}
              className="tw-w-full"
              formik={formik}
              required
              name={`reasonDescription`}
              isDisabled={isDisabled || formik.values.reason !== 'OTHER'}
              label="Description"
            />
          </div>
          <div className="tw-flex-1">
            <InputField
              fullWidth={true}
              formik={formik}
              required
              name={`amount`}
              min="0"
              max={sellerNetBalance}
              step=".01"
              type="number"
              isDisabled={isDisabled}
              label="Payment Amount"
            />
          </div>
        </div>

        <div className="tw-flex tw-flex-1 tw-flex-row">
          <div className="tw-flex-1"></div>
          <div className="tw-flex tw-flex-1 tw-flex-row">
            <div className="tw-flex-0 tw-ml-auto tw-h-16">
              <SubmitButton
                formik={formik}
                isSubmitting={formik.isSubmitting}
                isDisabled={isDisabled}
                submitLabel="Apply Payment"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
